;(() => {
  const namespace = (globalThis.StylistLocator = globalThis.StylistLocator || {})

  // ============================================================================
  // Dependencies
  // ============================================================================

  const { templates = {}, utils = {}, geometry = {} } = namespace
  const { buildListBadge = () => '' } = templates
  const { escapeHtml = (v) => (v == null ? '' : String(v)), encodeKey = (v) => v } = utils
  const { determineRoleCategory = () => 'default', hasValidStylistRole = () => false } = geometry

  // ============================================================================
  // List Rendering
  // ============================================================================

  /**
   * Render the stylist list to desktop and mobile containers.
   * @param {HTMLElement} listCount - Element to display count
   * @param {HTMLElement} listDesktop - Desktop list container
   * @param {HTMLElement} listMobile - Mobile list container
   * @param {Array} stylists - Array of stylists to render
   * @param {string} selectedStylistEmail - Currently selected stylist email
   * @param {Object} callbacks - Event callbacks
   * @param {number|null} matchingCount - Number of leading entries in `stylists`
   *   that match the user's preference filters. `null` means no preference
   *   filters are active and the list renders as a single section.
   */
  function renderList(listCount, listDesktop, listMobile, stylists, selectedStylistEmail, callbacks, matchingCount = null) {
    const { onListItemClick = () => {}, onCardClick = () => {} } = callbacks

    if (listCount) {
      const count = stylists.length
      listCount.innerHTML = formatStylistCount(count, matchingCount)
    }

    const markup = renderListMarkup(stylists, selectedStylistEmail, matchingCount)

    const attachClickHandlers = (container) => {
      container.querySelectorAll('[data-action="book"]').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.stopPropagation()
          onListItemClick(el.dataset.stylistEmail)
        })
      })
      container.querySelectorAll('.stylist-locator__profile-card').forEach((card) => {
        card.style.cursor = 'pointer'
        card.addEventListener('click', () => {
          onCardClick(card.dataset.stylistEmail)
        })
      })
    }

    if (listDesktop) {
      listDesktop.innerHTML = markup
      attachClickHandlers(listDesktop)
    }

    if (listMobile) {
      listMobile.innerHTML = markup
      attachClickHandlers(listMobile)
    }
  }

  /**
   * Generate HTML markup for the stylist list.
   * @param {Array} stylists - Array of stylists
   * @param {string} selectedStylistEmail - Currently selected stylist email
   * @param {number|null} matchingCount - Index where the "other stylists"
   *   section should start. `null` or equal to `stylists.length` means no
   *   separator is rendered.
   * @returns {string} HTML markup
   */
  function renderListMarkup(stylists, selectedStylistEmail, matchingCount = null) {
    if (stylists.length === 0) {
      return '<div class="stylist-locator__empty">No stylists found in this area yet.</div>'
    }

    const showSeparator =
      matchingCount !== null && matchingCount < stylists.length
    // When no matches at all, the separator sits above every card with a
    // label that doesn't imply there was a matching section above it.
    const separatorLabel = matchingCount === 0
      ? 'STYLISTS IN YOUR AREA'
      : 'OTHER STYLISTS IN YOUR AREA'
    const separatorMarkup = `
      <div class="stylist-locator__section-divider" role="presentation">
        <span class="stylist-locator__section-divider-line"></span>
        <span class="stylist-locator__section-divider-label">${separatorLabel}</span>
        <span class="stylist-locator__section-divider-line"></span>
      </div>
    `

    return stylists
      .map((stylist, index) => {
        const separator = showSeparator && index === matchingCount ? separatorMarkup : ''
        return separator + renderCard(stylist, selectedStylistEmail)
      })
      .join('')
  }

  function renderCard(stylist, selectedStylistEmail) {
        const emailKey = encodeKey(stylist.email)
        const name = escapeHtml(stylist.name || 'Unnamed Stylist')
        const isSalonOnly = stylist.isSalonOwner === true && !hasValidStylistRole(stylist)
        const role = escapeHtml(isSalonOnly ? 'Certified Salon' : (stylist.role || 'IBE® Stylist'))
        const isActive = stylist.email === selectedStylistEmail
        const roleCategory = stylist.roleCategory || determineRoleCategory(stylist)
        stylist.roleCategory = roleCategory
        const avatar = buildAvatarMarkup(stylist, name)
        const salonName = escapeHtml(stylist.salonName || stylist.salon_name || '')
        const address = escapeHtml(stylist.address || '')
        const tags = buildTagPills(stylist)
        const certBadge = buildCertBadgePill(role, roleCategory)
        const mastersLabel = buildMastersLabel(stylist)
        const galleryMarkup = buildProfileGallery(stylist)
        const descriptionMarkup = stylist.description
          ? `<p class="stylist-locator__profile-bio">${escapeHtml(stylist.description)}</p>`
          : ''
        const hasExpandContent = stylist.description || galleryMarkup

        return `
          <article class="stylist-locator__profile-card${isActive ? ' stylist-locator__profile-card--active' : ''}" data-stylist-email="${emailKey}">
            <div class="stylist-locator__profile-top">
              <div class="stylist-locator__profile-avatar">${avatar}</div>
              <div class="stylist-locator__profile-info">
                <div class="stylist-locator__profile-name-row">
                  <div class="stylist-locator__profile-names">
                    <span class="stylist-locator__profile-name">${name}</span>
                    ${salonName ? `<span class="stylist-locator__profile-salon">${salonName}</span>` : ''}
                  </div>
                  ${certBadge}
                </div>
                ${address ? `<div class="stylist-locator__profile-address stylist-locator__profile-address--inline">${buildAddressIcon(roleCategory)}<span>${address}</span></div>` : ''}
              </div>
            </div>
            ${address ? `<div class="stylist-locator__profile-address stylist-locator__profile-address--standalone">${buildAddressIcon(roleCategory)}<span>${address}</span></div>` : ''}
            <hr class="stylist-locator__profile-divider">
            <div class="stylist-locator__profile-details">
              ${mastersLabel}
              ${tags}
            </div>
            <div class="stylist-locator__profile-actions">
              <button type="button" class="stylist-locator__profile-btn stylist-locator__profile-btn--primary" data-stylist-email="${emailKey}" data-action="book">Book Consult</button>
              ${hasExpandContent ? `<button type="button" class="stylist-locator__profile-btn stylist-locator__profile-btn--secondary" data-action="learn">Learn More <svg width="10" height="6" viewBox="0 0 10 6" fill="none"><path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1" stroke-linecap="round"/></svg></button>` : ''}
            </div>
            ${hasExpandContent ? `
            <div class="stylist-locator__profile-expand">
              <div class="stylist-locator__profile-expand-inner">
                ${descriptionMarkup}
                ${galleryMarkup}
              </div>
            </div>
            ` : ''}
          </article>
        `
  }

  const ADDRESS_PIN_SVGS = {
    elite: '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="13" viewBox="0 0 20 25" fill="none"><path d="M10 1.25183e-08C7.36507 -0.000131737 4.83657 1.0397 2.96405 2.89348C1.09154 4.74726 0.0263493 7.26518 0 9.89997C0 16.7499 8.8125 24.3749 9.1875 24.6999C9.41392 24.8936 9.70206 25 10 25C10.2979 25 10.5861 24.8936 10.8125 24.6999C11.25 24.3749 20 16.7499 20 9.89997C19.9736 7.26518 18.9085 4.74726 17.0359 2.89348C15.1634 1.0397 12.6349 -0.000131737 10 1.25183e-08ZM10 13.75C9.13471 13.75 8.28885 13.4934 7.56938 13.0126C6.84991 12.5319 6.28916 11.8486 5.95803 11.0492C5.62689 10.2498 5.54025 9.37012 5.70906 8.52145C5.87787 7.67279 6.29455 6.89324 6.90641 6.28139C7.51826 5.66954 8.29781 5.25286 9.14648 5.08405C9.99515 4.91524 10.8748 5.00188 11.6742 5.33301C12.4737 5.66414 13.1569 6.2249 13.6377 6.94436C14.1184 7.66382 14.375 8.50968 14.375 9.37497C14.375 10.5353 13.9141 11.6481 13.0936 12.4686C12.2731 13.289 11.1603 13.75 10 13.75Z" fill="#C7AE78"/></svg>',
    masters: '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="13" viewBox="0 0 20 25" fill="none"><path d="M10 1.25183e-08C7.36507 -0.000131737 4.83657 1.0397 2.96405 2.89348C1.09154 4.74726 0.0263493 7.26518 0 9.89997C0 16.7499 8.8125 24.3749 9.1875 24.6999C9.41392 24.8936 9.70206 25 10 25C10.2979 25 10.5861 24.8936 10.8125 24.6999C11.25 24.3749 20 16.7499 20 9.89997C19.9736 7.26518 18.9085 4.74726 17.0359 2.89348C15.1634 1.0397 12.6349 -0.000131737 10 1.25183e-08ZM10 13.75C9.13471 13.75 8.28885 13.4934 7.56938 13.0126C6.84991 12.5319 6.28916 11.8486 5.95803 11.0492C5.62689 10.2498 5.54025 9.37012 5.70906 8.52145C5.87787 7.67279 6.29455 6.89324 6.90641 6.28139C7.51826 5.66954 8.29781 5.25286 9.14648 5.08405C9.99515 4.91524 10.8748 5.00188 11.6742 5.33301C12.4737 5.66414 13.1569 6.2249 13.6377 6.94436C14.1184 7.66382 14.375 8.50968 14.375 9.37497C14.375 10.5353 13.9141 11.6481 13.0936 12.4686C12.2731 13.289 11.1603 13.75 10 13.75Z" fill="#5E5E5E"/></svg>',
    certified_stylist: '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="13" viewBox="0 0 20 25" fill="none"><path d="M10 0C7.36507 -0.000131737 4.83657 1.0397 2.96405 2.89348C1.09154 4.74726 0.0263493 7.26518 0 9.89997C0 16.7499 8.8125 24.3749 9.1875 24.6999C9.41392 24.8936 9.70206 25 10 25C10.2979 25 10.5861 24.8936 10.8125 24.6999C11.25 24.3749 20 16.7499 20 9.89997C19.9736 7.26518 18.9085 4.74726 17.0359 2.89348C15.1634 1.0397 12.6349 -0.000131737 10 0Z" fill="#060606"/><circle cx="10" cy="9.375" r="4.375" fill="white"/></svg>',
    certified_salon: '<svg xmlns="http://www.w3.org/2000/svg" width="11" height="14" viewBox="-1 -1 22 27" fill="none"><path d="M10 0C7.36507 -0.000131737 4.83657 1.0397 2.96405 2.89348C1.09154 4.74726 0.0263493 7.26518 0 9.89997C0 16.7499 8.8125 24.3749 9.1875 24.6999C9.41392 24.8936 9.70206 25 10 25C10.2979 25 10.5861 24.8936 10.8125 24.6999C11.25 24.3749 20 16.7499 20 9.89997C19.9736 7.26518 18.9085 4.74726 17.0359 2.89348C15.1634 1.0397 12.6349 -0.000131737 10 0Z" fill="white" stroke="#333333" stroke-width="1.5" stroke-linejoin="round"/><circle cx="10" cy="9.375" r="4.375" fill="white" stroke="#060606" stroke-width="1.5"/></svg>',
    default: '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="13" viewBox="0 0 20 25" fill="none"><path d="M10 1.25183e-08C7.36507 -0.000131737 4.83657 1.0397 2.96405 2.89348C1.09154 4.74726 0.0263493 7.26518 0 9.89997C0 16.7499 8.8125 24.3749 9.1875 24.6999C9.41392 24.8936 9.70206 25 10 25C10.2979 25 10.5861 24.8936 10.8125 24.6999C11.25 24.3749 20 16.7499 20 9.89997C19.9736 7.26518 18.9085 4.74726 17.0359 2.89348C15.1634 1.0397 12.6349 -0.000131737 10 1.25183e-08ZM10 13.75C9.13471 13.75 8.28885 13.4934 7.56938 13.0126C6.84991 12.5319 6.28916 11.8486 5.95803 11.0492C5.62689 10.2498 5.54025 9.37012 5.70906 8.52145C5.87787 7.67279 6.29455 6.89324 6.90641 6.28139C7.51826 5.66954 8.29781 5.25286 9.14648 5.08405C9.99515 4.91524 10.8748 5.00188 11.6742 5.33301C12.4737 5.66414 13.1569 6.2249 13.6377 6.94436C14.1184 7.66382 14.375 8.50968 14.375 9.37497C14.375 10.5353 13.9141 11.6481 13.0936 12.4686C12.2731 13.289 11.1603 13.75 10 13.75Z" fill="#060606"/></svg>',
  }

  function buildAddressIcon(roleCategory) {
    return ADDRESS_PIN_SVGS[roleCategory] || ADDRESS_PIN_SVGS.default
  }

  function buildCertBadgePill(role, roleCategory) {
    if (!role || roleCategory === 'default') return ''
    const bgClass = roleCategory === 'elite' || roleCategory === 'certified_salon'
      ? 'stylist-locator__cert-badge--gold'
      : ''
    return `<span class="stylist-locator__cert-badge ${bgClass}">${role}</span>`
  }

  function deriveThumbUrl(fullUrl) {
    if (!fullUrl) return null
    if (fullUrl.includes('placehold.co')) return null
    if (fullUrl.includes('cdn.shopify.com')) return null
    return fullUrl.replace(/\.[a-z0-9]+$/i, '-thumb.jpg')
  }

  function buildProfileGallery(stylist) {
    const PLACEHOLDER = 'placehold.co/169x169'
    const images = [
      stylist.image_1, stylist.image_2, stylist.image_3,
      stylist.image_4, stylist.image_5,
    ].filter(src => src && !src.includes(PLACEHOLDER))

    if (images.length === 0) return ''

    const slides = images.map((src, i) => {
      const safeFull = escapeHtml(src)
      const thumb = deriveThumbUrl(src)
      const safeThumb = thumb ? escapeHtml(thumb) : safeFull
      const alt = escapeHtml(`${stylist.name || 'Stylist'} portfolio image ${i + 1}`)
      const fallbackAttrs = thumb
        ? ` data-full-src="${safeFull}" onerror="this.onerror=null;this.src=this.dataset.fullSrc;"`
        : ''
      return `<div class="stylist-locator__profile-gallery-thumb" data-gallery-slide data-image-index="${i}" data-image-src="${safeFull}"><img src="${safeThumb}" alt="${alt}" loading="lazy"${fallbackAttrs}></div>`
    }).join('')

    const showNav = images.length > 4

    return `
      <div class="stylist-locator__profile-gallery" data-gallery-root data-gallery-total="${images.length}">
        <div class="stylist-locator__profile-gallery-strip" data-gallery-viewport>
          <div class="stylist-locator__profile-gallery-track" data-gallery-track>
            ${slides}
          </div>
        </div>
        ${showNav ? `
        <div class="stylist-locator__profile-gallery-nav">
          <button type="button" class="stylist-locator__profile-gallery-btn" data-gallery-prev aria-label="Previous image">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <div class="stylist-locator__profile-gallery-progress">
            <div class="stylist-locator__profile-gallery-progress-fill" data-gallery-progress></div>
          </div>
          <button type="button" class="stylist-locator__profile-gallery-btn" data-gallery-next aria-label="Next image">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
          </button>
        </div>
        ` : ''}
      </div>
    `
  }

  function buildMastersLabel(stylist) {
    const hasMasters = stylist.business || stylist.cutting || stylist.color || stylist.technique
    if (!hasMasters) return ''
    return '<p class="stylist-locator__profile-masters-label">Graduate of Masters</p>'
  }

  function buildTagPills(stylist) {
    const tags = []
    if (stylist.business) tags.push('Business')
    if (stylist.cutting) tags.push('Cutting')
    if (stylist.color) tags.push('Color')
    if (stylist.technique) tags.push('Technique')
    if (tags.length === 0) return ''
    return `<div class="stylist-locator__profile-tags">${tags.map(t => `<span class="stylist-locator__profile-tag">${t}</span>`).join('')}</div>`
  }

  function formatStylistCount(count, matchingCount = null) {
    if (count === 0) {
      return '<strong>No results found</strong>'
    }
    if (matchingCount !== null) {
      const stylistsWord = count === 1 ? 'stylist' : 'stylists'
      const matchWord = matchingCount === 1 ? 'matches' : 'match'
      return `<strong>Showing ${count.toLocaleString()} ${stylistsWord}</strong> <span class="stylist-locator__results-count-note">(${matchingCount.toLocaleString()} ${matchWord} your filters)</span>`
    }
    if (count === 1) {
      return '<strong>Showing 1 result</strong>'
    }
    return `<strong>Showing all ${count.toLocaleString()} results</strong>`
  }

  function getInitials(name) {
    return (name || '')
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0])
      .join('')
      .slice(0, 2)
  }

  function buildAvatarMarkup(stylist, safeName) {
    if (stylist.profile_photo) {
      const avatarSrc = escapeHtml(stylist.profile_photo)
      return `<img src="${avatarSrc}" alt="${safeName}">`
    }

    const initials = getInitials(stylist.name)
    return `<span>${initials || 'IB'}</span>`
  }

  // ============================================================================
  // Exports
  // ============================================================================

  function initProfileGallery(card) {
    const gallery = card.querySelector('.stylist-locator__profile-gallery')
    if (!gallery || gallery._initialized) return
    gallery._initialized = true

    const strip = gallery.querySelector('.stylist-locator__profile-gallery-strip')
    const track = gallery.querySelector('.stylist-locator__profile-gallery-track')
    const prevBtn = gallery.querySelector('[data-gallery-prev]')
    const nextBtn = gallery.querySelector('[data-gallery-next]')
    const progressFill = gallery.querySelector('[data-gallery-progress]')
    const thumbs = Array.from(gallery.querySelectorAll('.stylist-locator__profile-gallery-thumb'))

    if (!strip || !track) return

    // Image modal click
    const { gallery: galleryModule = {} } = namespace
    const images = thumbs.map(t => t.dataset.imageSrc).filter(Boolean)
    if (images.length > 0 && galleryModule.showImageModal) {
      thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', (e) => {
          e.stopPropagation()
          galleryModule.showImageModal(images, i)
        })
      })
    }

    if (!prevBtn || !nextBtn) return

    let progressRafId = null
    const updateProgress = () => {
      if (progressRafId) return
      progressRafId = requestAnimationFrame(() => {
        progressRafId = null
        if (!progressFill || !strip) return
        const maxScroll = strip.scrollWidth - strip.clientWidth
        if (maxScroll <= 0) {
          progressFill.style.width = '100%'
          return
        }
        const ratio = strip.scrollLeft / maxScroll
        const fillWidth = Math.max(30, (strip.clientWidth / strip.scrollWidth) * 100)
        const remaining = 100 - fillWidth
        progressFill.style.width = fillWidth + '%'
        progressFill.style.marginLeft = (ratio * remaining) + '%'
      })
    }

    const scrollStep = 81 // thumb width + gap

    prevBtn.addEventListener('click', (e) => {
      e.stopPropagation()
      strip.scrollBy({ left: -scrollStep * 2, behavior: 'smooth' })
    })

    nextBtn.addEventListener('click', (e) => {
      e.stopPropagation()
      strip.scrollBy({ left: scrollStep * 2, behavior: 'smooth' })
    })

    strip.addEventListener('scroll', updateProgress)
    updateProgress()
  }

  function setupLearnMoreHandlers(container) {
    if (!container) return
    container.querySelectorAll('[data-action="learn"]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation()
        const card = btn.closest('.stylist-locator__profile-card')
        if (!card) return

        const isExpanded = card.classList.toggle('stylist-locator__profile-card--expanded')
        const textSpan = btn.firstChild
        if (textSpan) textSpan.textContent = isExpanded ? 'Show Less ' : 'Learn More '

        if (isExpanded) {
          initProfileGallery(card)
        }
      })
    })
  }

  namespace.list = Object.assign({}, namespace.list, {
    renderList,
    renderListMarkup,
    setupLearnMoreHandlers,
  })
})()
